// Tiny RRULE → human string. Handles the common cases; everything else
// collapses to "Recurring event". We intentionally don't pull in a full
// RRULE library — the dashboard only needs to display recurrence, never edit.

const DAY_NAMES: Record<string, string> = {
  MO: 'Mon',
  TU: 'Tue',
  WE: 'Wed',
  TH: 'Thu',
  FR: 'Fri',
  SA: 'Sat',
  SU: 'Sun',
};

const ordinal = (n: number): string => {
  const v = Math.abs(n);
  if (v % 100 >= 11 && v % 100 <= 13) return `${n}th`;
  const last = v % 10;
  if (last === 1) return `${n}st`;
  if (last === 2) return `${n}nd`;
  if (last === 3) return `${n}rd`;
  return `${n}th`;
};

const parseRule = (rule: string): Record<string, string> => {
  const body = rule.replace(/^RRULE:/i, '');
  const out: Record<string, string> = {};
  for (const part of body.split(';')) {
    const [k, v] = part.split('=');
    if (k && v) out[k.toUpperCase()] = v;
  }
  return out;
};

export const recurrenceCopy = (recurrence: string[] | undefined | null): string | null => {
  if (!recurrence || recurrence.length === 0) return null;
  const rule = recurrence.find((r) => r.toUpperCase().startsWith('RRULE'));
  if (!rule) return 'Recurring event';

  const parts = parseRule(rule);
  const freq = parts.FREQ;
  const interval = parts.INTERVAL ? parseInt(parts.INTERVAL, 10) : 1;

  if (!freq) return 'Recurring event';

  switch (freq) {
    case 'DAILY':
      return interval > 1 ? `Repeats every ${interval} days` : 'Repeats daily';
    case 'WEEKLY': {
      const byDay = parts.BYDAY?.split(',')
        .map((d) => DAY_NAMES[d.toUpperCase()])
        .filter(Boolean);
      const base = interval > 1 ? `Repeats every ${interval} weeks` : 'Repeats weekly';
      return byDay && byDay.length ? `${base} on ${byDay.join(', ')}` : base;
    }
    case 'MONTHLY': {
      const dom = parts.BYMONTHDAY ? parseInt(parts.BYMONTHDAY, 10) : null;
      const base = interval > 1 ? `Repeats every ${interval} months` : 'Repeats monthly';
      return dom ? `${base} on the ${ordinal(dom)}` : base;
    }
    case 'YEARLY':
      return interval > 1 ? `Repeats every ${interval} years` : 'Repeats yearly';
    default:
      return 'Recurring event';
  }
};
